import React, { useState, useEffect } from "react";
import {
  TextField,
  Checkbox,
  Button,
  IconButton,
  Grid2 as Grid,
  Grid2Props,
} from "@mui/material";

import {
  convertToComparableValue,
  validateDateInput,
} from "../../../../utils/intellexusDateUtils";
import {
  IntellexusDateEntry,
  IntellexusDateStructure,
} from "../../../../types/intellexusDate";
import FormLabel from "../base/FormLabel";
import { singleGridItem } from "../../fields/defaultStyles";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useFormHandlerContext } from "../../../../controllers/formController";
import { Controller, useFormContext } from "react-hook-form";
import { useInputFullName } from "../../fields/subForms/NamePrefixContext";
import IntellexusDateTooltip from "./IntellexusDateTooltip";
import SubForm from "../../fields/subForms/SubForm";

interface IntellexusDateFieldProps extends Grid2Props {
  name: string;
  label: string;
  disabled?: boolean;
}

interface UIRangeEntry {
  from: string;
  to: string;
  selected: boolean;
}

interface RangeEntry {
  from: string[];
  to: string[];
  certainty_from: boolean[];
  certainty_to: boolean[];
}

interface TransformedResult {
  ranges: RangeEntry[];
  selected_date: {
    from: number | null;
    to: number | null;
    certainty_from: boolean | null;
    certainty_to: boolean | null;
  };
  selected_index: number | null;
}

function parseDateField(rawStr: string): {
  values: string[];
  certainties: boolean[];
} {
  const result = { values: [] as string[], certainties: [] as boolean[] };

  // Remove leading/trailing spaces; no need to remove '?' here because we want to detect it
  const str = rawStr.trim();

  // 1) If the string includes slashes => multiple possibilities
  if (str.includes("/")) {
    const parts = str.split("/").map((p) => p.trim());
    parts.forEach((part) => {
      const isUncertain = part.includes("?");
      result.certainties.push(!isUncertain);
      // Remove '?' before storing the actual date
      result.values.push(part.replace("?", "").trim());
    });
  }
  // 2) If there's no slash
  else if (str !== "") {
    const isUncertain = str.includes("?");
    result.certainties.push(!isUncertain);
    result.values.push(str.replace("?", "").trim());
  }

  return result;
}

function handleCenturies(
  fromValues: string[],
  toValues: string[],
  fromCertainties: boolean[],
  toCertainties: boolean[],
  isFromField: boolean
): string[] {
  return fromValues.map((val) => {
    if (val.toLowerCase().includes("cent")) {
      const isNegative = val.startsWith("-");
      const centuryNumber =
        parseInt(
          val
            .replace(/ca\.?\s*/i, "")
            .replace(/cent\.?/i, "")
            .trim(),
          10
        ) - 1;
      const fromNum = isNegative ? -centuryNumber * 100 : centuryNumber * 100;
      const toNum = isNegative
        ? -centuryNumber * 100 - 1
        : centuryNumber * 100 + 99;
      if (isFromField) {
        toValues.push(`${toNum}`);
        toCertainties.push(true); // or false if you consider centuries uncertain
      } else {
        fromValues.push(`${fromNum}`);
        fromCertainties.push(true);
      }
      return `${fromNum}`;
    }
    return val;
  });
}

function transformSingleEntry(entry: IntellexusDateEntry): RangeEntry {
  const { from, to } = entry;

  // Parse "from" field
  const parsedFrom = parseDateField(from);
  // Parse "to" field
  const parsedTo = parseDateField(to);

  // Century conversions for "from"
  const updatedFromValues = handleCenturies(
    parsedFrom.values,
    parsedTo.values,
    parsedFrom.certainties,
    parsedTo.certainties,
    true // means "from" field
  );
  // Century conversions for "to"
  const updatedToValues = handleCenturies(
    parsedTo.values,
    updatedFromValues,
    parsedTo.certainties,
    parsedFrom.certainties,
    false // means "to" field
  );

  return {
    from: updatedFromValues,
    to: updatedToValues,
    certainty_from: parsedFrom.certainties,
    certainty_to: parsedTo.certainties,
  };
}

function rawDataToUIRanges(data: IntellexusDateStructure): UIRangeEntry[] {
  if (!data || !Array.isArray(data.ranges)) {
    return [{ from: "", to: "", selected: false }];
  }

  return data.ranges.map((range, index) => {
    const fromStr =
      range.from
        .map((val, i) => (range.certainty_from[i] ? val : `${val}?`))
        .join("/") || "";

    const toStr =
      range.to
        .map((val, i) => (range.certainty_to[i] ? val : `${val}?`))
        .join("/") || "";

    return {
      from: fromStr,
      to: toStr,
      selected: data.selected_index === index,
    };
  });
}

const IntellexusDateField: React.FC<IntellexusDateFieldProps> = ({
  name,
  label,
  disabled = false,
  ...grid
}) => {
  const { isProcessing } = useFormHandlerContext();
  const { setValue, getValues, control } = useFormContext();
  const fullName = useInputFullName(name);

  const [ranges, setRanges] = useState<UIRangeEntry[]>(() =>
    rawDataToUIRanges(getValues(fullName) as IntellexusDateStructure)
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const newRawData = transformRanges(ranges);
    setValue(fullName, newRawData);
  }, [ranges, fullName, setValue]);

  const handleFieldChange = (
    index: number,
    field: "from" | "to" | "selected",
    value: string | boolean
  ) => {
    const updatedRanges = [...ranges];
    const errors = { ...validationErrors };

    if (field === "selected") {
      updatedRanges.forEach((_, i) => (updatedRanges[i].selected = false));
      updatedRanges[index].selected = value as boolean;
    } else {
      updatedRanges[index][field] = value as string;

      const isValidInput = validateDateInput(value as string);

      if (!isValidInput) {
        errors[`${field}-${index}`] =
          "Invalid format. Use YYYY, YYYY/YYYY, -YYYY, -YYYY/-YYYY, 11th cent., or dd/mm/yyyy. You may also use '?' for uncertainty.";
      } else {
        errors[`${field}-${index}`] = "";

        const fromValue = convertToComparableValue(
          updatedRanges[index].from.replace(/\?/g, "").trim(),
          true
        );
        const toValue = convertToComparableValue(
          updatedRanges[index].to.replace(/\?/g, "").trim(),
          false
        );

        if (
          fromValue !== null &&
          toValue !== null &&
          fromValue !== false &&
          toValue !== false &&
          fromValue > toValue
        ) {
          errors[`to-${index}`] =
            "The 'to' date must be greater than the 'from' date.";
        } else {
          if (
            errors[`to-${index}`] ===
            "The 'to' date must be greater than the 'from' date."
          ) {
            errors[`to-${index}`] = "";
          }
        }
        const isDuplicate = updatedRanges.some(
          (entry, idx) =>
            idx !== index &&
            entry.from === updatedRanges[index].from &&
            entry.to === updatedRanges[index].to
        );
        if (isDuplicate) {
          errors[`from-${index}`] =
            "Duplicate range detected. Please enter unique values.";
          errors[`to-${index}`] =
            "Duplicate range detected. Please enter unique values.";
        } else {
          if (
            errors[`from-${index}`] ===
            "Duplicate range detected. Please enter unique values."
          ) {
            errors[`from-${index}`] = "";
          }
          if (
            errors[`to-${index}`] ===
            "Duplicate range detected. Please enter unique values."
          ) {
            errors[`to-${index}`] = "";
          }
        }
      }
    }

    setRanges(updatedRanges);
    setValidationErrors(errors);
  };

  const addRangeRow = () => {
    setRanges([...ranges, { from: "", to: "", selected: false }]);
  };

  const transformRanges = (
    entries: IntellexusDateEntry[] | null | undefined
  ): TransformedResult => {
    const transformed: TransformedResult = {
      ranges: [],
      selected_date: {
        from: null,
        to: null,
        certainty_from: null,
        certainty_to: null,
      },
      selected_index: null,
    };

    if (!Array.isArray(entries)) {
      return transformed; // return empty structure if invalid
    }

    entries.forEach((entry, index) => {
      const rangeEntry = transformSingleEntry(entry);
      transformed.ranges.push(rangeEntry);

      // If this entry is "selected", set the selected_index & selected_date
      if (entry.selected) {
        transformed.selected_index = index;

        // Convert each from/to value to a comparable number
        const fromNumbers = rangeEntry.from
          .map((val) => convertToComparableValue(val.replace(/\?/g, "").trim()))
          .filter((n) => n !== null && n !== false) as number[];

        const toNumbers = rangeEntry.to
          .map((val) => convertToComparableValue(val.replace(/\?/g, "").trim()))
          .filter((n) => n !== null && n !== false) as number[];

        const minFrom = fromNumbers.length
          ? Math.floor(Math.min(...fromNumbers) / 10000)
          : null;
        const maxTo = toNumbers.length
          ? Math.floor(Math.max(...toNumbers) / 10000)
          : null;

        transformed.selected_date = {
          from: fromNumbers.length > 0 ? minFrom : null,
          to: toNumbers.length > 0 ? maxTo : null,
          certainty_from: rangeEntry.certainty_from.every(Boolean),
          certainty_to: rangeEntry.certainty_to.every(Boolean),
        };
      }
    });

    return transformed;
  };

  const removeRangeRow = (index: number) => {
    const updatedRanges = ranges.filter((_, i) => i !== index);
    setRanges(updatedRanges);
  };

  return (
    <Grid {...singleGridItem} {...grid} id={fullName}>
      <FormLabel
        label={label}
        name={name}
        leading={<IntellexusDateTooltip />}
      />
      <Controller
        name={fullName}
        control={control}
        rules={{
          validate: () => {
            const hasAnyError = Object.values(validationErrors).some(
              (msg) => !!msg
            );
            if (hasAnyError) {
              return "Date range validation failed. Please fix errors.";
            }
            return true;
          },
        }}
        render={() => (
          <SubForm name={name}>
            <Grid
              container
              spacing={2}
              alignItems="start"
              {...singleGridItem}
              {...grid}
            >
              {Array.isArray(ranges) &&
                ranges.length > 0 &&
                ranges.map((range, index) => (
                  <Grid
                    container
                    key={index}
                    spacing={1}
                    size={12}
                    columns={11}
                  >
                    <Grid size={5}>
                      <FormLabel
                        label="From"
                        name={`${fullName}.ranges.${index}.from`}
                      />
                      <TextField
                        name={`${fullName}.ranges.${index}.from`}
                        placeholder="From"
                        value={range.from}
                        onChange={(e) =>
                          handleFieldChange(index, "from", e.target.value)
                        }
                        error={!!validationErrors[`from-${index}`]} // Highlight invalid inputs
                        helperText={validationErrors[`from-${index}`]}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <FormLabel
                        label="To"
                        name={`${fullName}.ranges.${index}.to`}
                      />
                      <TextField
                        name={`${fullName}.ranges.${index}.to`}
                        placeholder="To"
                        value={range.to}
                        onChange={(e) =>
                          handleFieldChange(index, "to", e.target.value)
                        }
                        error={!!validationErrors[`to-${index}`]} // Highlight invalid inputs
                        helperText={validationErrors[`to-${index}`]}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1} alignSelf="start" display="flex" mt={4}>
                      <Checkbox
                        checked={range.selected}
                        onChange={() =>
                          handleFieldChange(index, "selected", !range.selected)
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() => removeRangeRow(index)}
                        size="small"
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              <Grid {...singleGridItem}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  startIcon={<AddCircleOutline />}
                  disabled={disabled || isProcessing}
                  onClick={() => addRangeRow()}
                >
                  Add Range
                </Button>
              </Grid>
            </Grid>
          </SubForm>
        )}
      />
    </Grid>
  );
};

const GetIntellexusDate = (intellexusDate: IntellexusDateStructure): string => {
  if (
    !intellexusDate ||
    !intellexusDate.ranges ||
    !Array.isArray(intellexusDate.ranges) ||
    intellexusDate.ranges.length === 0
  ) {
    return "Unknown";
  }

  const formattedRanges = intellexusDate.ranges.map((range) => {
    const fromParts = range.from.map((from, idx) =>
      range.certainty_from[idx] ? from : `${from}?`
    );

    const toParts = range.to.map((to, idx) =>
      range.certainty_to[idx] ? to : `${to}?`
    );

    const fromFormatted = fromParts.length > 0 ? fromParts.join("/") : null;
    const toFormatted = toParts.length > 0 ? toParts.join("/") : null;

    let formatted = "";
    if (fromFormatted && toFormatted) {
      if (fromFormatted === toFormatted) {
        formatted = fromFormatted; // No need for a range if both are the same
      } else {
        formatted = `${fromFormatted} - ${toFormatted}`;
      }
    } else if (fromFormatted) {
      formatted = `Not after ${fromFormatted}`;
    } else if (toFormatted) {
      formatted = `Not before ${toFormatted}`;
    }

    return formatted;
  });

  if (intellexusDate.selected_index !== null) {
    return formattedRanges[intellexusDate.selected_index];
  }
  return formattedRanges.join(", ");
};

const DEFAULT_INTELLEXUS_DATE: IntellexusDateStructure = {
  ranges: [],
  selected_index: null,
  selected_date: {
    from: null,
    to: null,
    certainty_from: null,
    certainty_to: null,
  },
};

export { IntellexusDateField, GetIntellexusDate, DEFAULT_INTELLEXUS_DATE };
